import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listingsApi, type ListingMergeRequest } from '../../api/listings';
import PlatformBadges from '../../components/listings/PlatformBadges';
import { useModuleVisibility } from '../../hooks/useConfigVisibility';
import type { Listing } from '../../types/index';

type MergeField = {
  key: keyof Listing;
  label: string;
};
type SectionMode = 'link' | 'individual' | 'none';
type ObjectField = 'adresse_id' | 'haus_id' | 'einheit_id' | 'vermieter_id' | 'verfahren_id';
const OBJECT_QUESTIONS: Record<ObjectField, string> = {
  adresse_id: 'Welche Adresse soll übernommen werden?',
  haus_id: 'Welches Haus soll übernommen werden?',
  einheit_id: 'Welches Objekt soll übernommen werden?',
  vermieter_id: 'Welcher Vermieter soll übernommen werden?',
  verfahren_id: 'Welches Verfahren soll übernommen werden?',
};

const FIELD_GROUPS: Array<{ key: string; title: string; fields: MergeField[]; objectField?: ObjectField; module?: string }> = [
  {
    key: 'fallbearbeitung',
    title: 'Fallbearbeitung',
    fields: [
      { key: 'state', label: 'Status' },
      { key: 'notes', label: 'Notizen' },
      { key: 'responsible_person', label: 'Verantwortliche Person' },
      { key: 'zustaendig_user_id', label: 'Zuständigkeit' },
    ],
  },
  {
    key: 'inseratdetails',
    title: 'Inseratdetails',
    fields: [
      { key: 'title', label: 'Titel' },
      { key: 'description', label: 'Beschreibung' },
      { key: 'reviews', label: 'Bewertungen' },
      { key: 'rating', label: 'Bewertung' },
      { key: 'price_per_night', label: 'Preis pro Nacht' },
      { key: 'currency', label: 'Währung' },
    ],
  },
  {
    key: 'vermieter',
    title: 'Vermieter',
    objectField: 'vermieter_id',
    module: 'vermieter',
    fields: [
      { key: 'vermieter_id', label: 'Verknüpfter Vermieter' },
      { key: 'host_name', label: 'Gastgeber' },
      { key: 'host_type', label: 'Gastgeber-Typ' },
    ],
  },
  {
    key: 'adresse',
    title: 'Adresse',
    objectField: 'adresse_id',
    module: 'adressen',
    fields: [
      { key: 'adresse_id', label: 'Bestätigter Standort' },
      { key: 'address', label: 'Adresse' },
      { key: 'neighborhood', label: 'Umgebung' },
      { key: 'baugebiet', label: 'Baugebiet' },
      { key: 'city', label: 'Ort' },
      { key: 'zipcode', label: 'Postleitzahl' },
      { key: 'address_verified', label: 'Adresse bestätigt' },
    ],
  },
  { key: 'haus', title: 'Haus', objectField: 'haus_id', module: 'haeuser', fields: [{ key: 'haus_id', label: 'Haus' }] },
  {
    key: 'objekt', title: 'Objekt', objectField: 'einheit_id', module: 'objekte',
    fields: [
      { key: 'einheit_id', label: 'Objekt' },
      { key: 'registration_number', label: 'Registrierungsnummer' },
      { key: 'listing_type', label: 'Typ' },
      { key: 'size', label: 'Größe' },
      { key: 'size_sqm', label: 'Größe in m²' },
      { key: 'guests_count', label: 'Gäste' },
      { key: 'bedrooms', label: 'Schlafzimmer' },
      { key: 'beds_count', label: 'Betten' },
      { key: 'bathrooms_count', label: 'Bäder' },
    ],
  },
  { key: 'verfahren', title: 'Verfahren', objectField: 'verfahren_id', module: 'verfahren', fields: [{ key: 'verfahren_id', label: 'Verfahren' }] },
  { key: 'bebauungsplaene', title: 'Bebauungspläne', module: 'bebauungsplaene', fields: [] },
  { key: 'gruppen', title: 'Gruppen', module: 'gruppen', fields: [] },
];

const LINKED_ADDRESS_FIELDS: Array<keyof Listing> = [
  'adresse_id', 'address', 'city', 'zipcode',
  'address_verified',
];
const EDITABLE_FIELDS = ['address', 'baugebiet', 'city', 'zipcode', 'host_name', 'host_type'];
const STAGES = ['Duplikat prüfen', 'Unterschiede klären', 'Ergebnis prüfen'];

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Nicht vorhanden';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  return String(value);
}

export default function InserateMerge() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const modules = useModuleVisibility();
  const tabs = FIELD_GROUPS.filter(group => !group.module || modules[group.module] !== false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const stepIndex = Math.max(0, tabs.findIndex(group => group.key === searchParams.get('step')));
  const activeGroup = tabs[stepIndex];
  const goToStep = (index: number) => {
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.set('step', tabs[index].key);
      return next;
    });
    headingRef.current?.scrollIntoView({ block: 'start' });
  };
  const sourceIds = (searchParams.get('ids') || '')
    .split(',')
    .map(Number)
    .filter(Number.isInteger);
  const validIds = sourceIds.length === 2 && sourceIds.every(id => id > 0) && sourceIds[0] !== sourceIds[1];
  const [fieldSourceOverrides, setFieldSourceOverrides] = useState<Record<string, number>>({});
  const [objectModes, setObjectModes] = useState<Partial<Record<ObjectField, SectionMode>>>({});
  const [collectionSelections, setCollectionSelections] = useState<Partial<Record<'linked_groups' | 'linked_buildingplans', number[]>>>({});
  const [fieldValueOverrides, setFieldValueOverrides] = useState<NonNullable<ListingMergeRequest['field_values']>>({});
  const [stage, setStage] = useState(0);
  const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);
  const [showAllFields, setShowAllFields] = useState(false);
  const [showAllSections, setShowAllSections] = useState(false);
  const [reviewedSections, setReviewedSections] = useState<string[]>([]);
  const goToStage = (next: number) => {
    setStage(next);
    headingRef.current?.scrollIntoView({ block: 'start' });
  };

  const { data: listings, isLoading, error } = useQuery({
    queryKey: ['listing-merge', sourceIds],
    queryFn: () => Promise.all(sourceIds.map(listingsApi.getListing)),
    enabled: validIds,
    staleTime: 0,
  });

  const getObjectMode = (field: ObjectField): SectionMode | null => {
    const group = FIELD_GROUPS.find(item => item.objectField === field);
    const linkedIds = new Set(listings?.map(listing => listing[field]).filter(id => id != null));
    const hasObject = linkedIds.size > 0;
    if (group?.module && modules[group.module] === false) return hasObject ? 'link' : 'none';
    return objectModes[field] ?? (linkedIds.size === 1 ? 'link' : hasObject ? null : field === 'adresse_id' || field === 'vermieter_id' ? 'individual' : 'none');
  };
  const addressMode = getObjectMode('adresse_id');
  const landlordMode = getObjectMode('vermieter_id');
  const fieldValues = Object.fromEntries(Object.entries(fieldValueOverrides).filter(([key]) => (
    key === 'host_name' || key === 'host_type' ? landlordMode === 'individual' : addressMode === 'individual'
  )));
  const fieldSources: Record<string, number> = {};
  if (listings) {
    for (const group of FIELD_GROUPS) {
      for (const field of group.fields) {
        const preferred = listings.find(listing => {
          const value = field.key === 'size' ? listing.size || listing.size_sqm : listing[field.key];
          return value !== null && value !== undefined && value !== '';
        });
        fieldSources[field.key] = fieldSourceOverrides[field.key]
          ?? preferred?.id
          ?? listings[0].id;
      }
    }
    const addressSource = listings.find(listing => listing.id === fieldSources.adresse_id);
    if (addressMode === 'link' && addressSource?.adresse_id != null) {
      for (const field of LINKED_ADDRESS_FIELDS) fieldSources[field] = addressSource.id;
    }
    if (landlordMode === 'link') {
      fieldSources.host_name = fieldSources.vermieter_id;
      fieldSources.host_type = fieldSources.vermieter_id;
    }
    fieldSources.size_sqm = fieldSources.size;
  }

  const mergeRequest: ListingMergeRequest = {
      source_listing_ids: sourceIds,
      field_sources: fieldSources,
      address_mode: addressMode === 'link' ? 'link' : 'individual',
      landlord_mode: landlordMode === 'link' ? 'link' : 'individual',
      field_values: fieldValues,
      house_mode: modules.haeuser === false ? undefined : getObjectMode('haus_id') === 'link' ? 'link' : 'none',
      object_mode: modules.objekte === false ? undefined : getObjectMode('einheit_id') === 'link' ? 'link' : 'none',
      procedure_mode: modules.verfahren === false ? undefined : getObjectMode('verfahren_id') === 'link' ? 'link' : 'none',
      gruppe_ids: collectionSelections.linked_groups,
      bebauungsplan_ids: collectionSelections.linked_buildingplans,
      verify_buildingplans: true,
  };
  const { data: mergePreview, error: previewError, isFetching: previewLoading, refetch: refreshPreview } = useQuery({
    queryKey: ['listing-merge-preview', mergeRequest],
    queryFn: () => listingsApi.previewMerge(mergeRequest),
    enabled: duplicateConfirmed && !!listings && tabs.every(group => !group.objectField || getObjectMode(group.objectField) !== null),
    retry: false,
    staleTime: 0,
  });
  const mergeMutation = useMutation({
    mutationFn: () => listingsApi.mergeListings({ ...mergeRequest, preview_signature: mergePreview?.signature }),
    onSuccess: ({ merged_listing_id }) => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
      queryClient.invalidateQueries({ queryKey: ['listing-merge'] });
      navigate(`/inserat/${merged_listing_id}`);
    },
  });
  const rejectMutation = useMutation({
    mutationFn: () => listingsApi.rejectDuplicate(sourceIds[0], sourceIds[1]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
      queryClient.invalidateQueries({ queryKey: ['listing-merge'] });
      navigate(`/inserat/${sourceIds[0]}`);
    },
  });

  if (!validIds) {
    return <div className="mx-auto max-w-4xl p-6 text-red-700">Für die Zusammenführung werden genau zwei Inserate benötigt.</div>;
  }
  if (isLoading) return <div className="mx-auto max-w-6xl p-6 text-gray-500">Lädt…</div>;
  if (error || !listings) {
    return <div className="mx-auto max-w-4xl p-6 text-red-700">Die Inserate konnten nicht geladen werden.</div>;
  }
  if (!listings[0].duplicate_candidates?.some(candidate => candidate.id === sourceIds[1])) {
    return <div role="alert" className="mx-auto max-w-4xl p-6 text-red-700">Dieses Duplikat-Paar ist nicht mehr zur Prüfung verfügbar.</div>;
  }
  const addressSource = listings.find(listing => listing.id === fieldSources.adresse_id);
  const objectField = activeGroup.objectField;
  const sectionMode = objectField ? getObjectMode(objectField) : null;
  const selectedAddressId = addressMode === 'link' ? addressSource?.adresse_id : null;
  const houseSource = listings.find(listing => listing.id === fieldSources.haus_id);
  const selectedHouseId = getObjectMode('haus_id') === 'link' ? houseSource?.haus_id : null;
  const savedObjects = objectField ? listings.filter((listing, index, all) => listing[objectField] != null
    && all.findIndex(item => item[objectField] === listing[objectField]) === index) : [];
  const compatibleObject = (listing: Listing) => objectField === 'haus_id'
    ? (listing.linked_object_parents?.haus_id ?? listing.adresse_id) === selectedAddressId || listing.adresse_id != null
    : objectField === 'einheit_id' ? (listing.linked_object_parents?.einheit_id ?? listing.haus_id) === selectedHouseId
      || (listing.haus_id != null && listing.adresse_id != null) : true;
  const setSectionMode = (mode: SectionMode) => {
    if (objectField) setObjectModes(current => ({ ...current, [objectField]: mode }));
  };
  const selectedUnit = listings.find(listing => listing.id === fieldSources.einheit_id);
  const savedUnitValues = selectedUnit?.linked_unit_values ?? {};
  const visibleFields = activeGroup.fields.filter(field => {
    if (field.key === 'size_sqm') return false;
    if (objectField === 'einheit_id') return field.key !== 'einheit_id' && (sectionMode !== 'link'
      || !Object.hasOwn(savedUnitValues, field.key));
    if (field.key === 'neighborhood' || field.key === 'baugebiet') return true;
    return !objectField || (sectionMode === 'individual'
      && !['adresse_id', 'haus_id', 'einheit_id', 'vermieter_id', 'address_verified'].includes(field.key));
  });
  const unresolvedTabs = tabs.filter(group => group.objectField && getObjectMode(group.objectField) === null);
  const invalidHouse = modules.haeuser !== false && getObjectMode('haus_id') === 'link' && (houseSource?.linked_object_parents?.haus_id ?? houseSource?.adresse_id) !== selectedAddressId;
  const invalidObject = modules.objekte !== false && getObjectMode('einheit_id') === 'link' && (listings.find(listing => listing.id === fieldSources.einheit_id)?.linked_object_parents?.einheit_id
      ?? listings.find(listing => listing.id === fieldSources.einheit_id)?.haus_id) !== selectedHouseId;
  const invalidHierarchy = invalidHouse || invalidObject;
  const missingObjectChoice = unresolvedTabs.length > 0 || invalidHierarchy;
  const collectionKey = activeGroup.key === 'gruppen' ? 'linked_groups' : activeGroup.key === 'bebauungsplaene' ? 'linked_buildingplans' : null;
  const collectionObjects = collectionKey === 'linked_buildingplans' ? mergePreview?.buildingplans ?? []
    : collectionKey ? [...new Map(listings.flatMap(listing => listing[collectionKey] ?? []).map(item => [item.id, item])).values()] : [];
  const selectedCollectionIds = collectionKey ? collectionSelections[collectionKey] ?? collectionObjects.map(item => item.id) : [];
  const valueForField = (listing: Listing, field: keyof Listing) => field === 'size'
    ? listing.size || (listing.size_sqm != null ? `${listing.size_sqm} m²` : null) : listing[field];
  const fieldConflict = (field: MergeField) => {
    const values = listings.map(listing => valueForField(listing, field.key)).filter(value => value != null && value !== '');
    return new Set(values.map(value => JSON.stringify(value))).size > 1;
  };
  const sectionNeedsDecision = (group: typeof activeGroup) => {
    if ((group.objectField === 'haus_id' && invalidHouse) || (group.objectField === 'einheit_id' && invalidObject)) return true;
    if (group.objectField && getObjectMode(group.objectField) === null) return true;
    const linked = group.objectField && getObjectMode(group.objectField) === 'link';
    return group.fields.some(field => {
      if (field.key === 'size_sqm' || field.key.endsWith('_id')) return false;
      if (linked && group.objectField === 'einheit_id' && Object.hasOwn(savedUnitValues, field.key)) return false;
      if (linked && group.objectField === 'adresse_id' && LINKED_ADDRESS_FIELDS.includes(field.key)) return false;
      if (linked && group.objectField === 'vermieter_id' && (field.key === 'host_name' || field.key === 'host_type')) return false;
      return fieldConflict(field);
    }) && !reviewedSections.includes(group.key);
  };
  const pendingSections = tabs.filter(sectionNeedsDecision);
  const resultValue = (field: MergeField) => {
    if (mergePreview) return mergePreview.linked_object_labels[field.key] ?? mergePreview.values[field.key];
    if (Object.hasOwn(fieldValues, field.key)) return fieldValues[field.key];
    if (getObjectMode('einheit_id') === 'link' && Object.hasOwn(savedUnitValues, field.key)) return savedUnitValues[field.key as keyof typeof savedUnitValues];
    if (field.key === 'address_verified' && addressMode !== 'link') return false;
    if (field.key === 'size_sqm') return valueForField(listings.find(listing => listing.id === fieldSources.size)!, field.key);
    const source = listings.find(listing => listing.id === fieldSources[field.key])!;
    return source.linked_object_labels?.[field.key] ?? valueForField(source, field.key);
  };
  const imagesForListing = (listing: Listing): string[] => {
    try {
      const images: unknown = JSON.parse(listing.image_urls || '[]');
      return Array.isArray(images)
        ? [...new Set(images.filter((image: unknown): image is string => typeof image === 'string' && image.length > 0))]
        : [];
    } catch {
      return [];
    }
  };
  const duplicateCandidate = listings[0].duplicate_candidates?.find(
    candidate => candidate.id === listings[1].id,
  );
  const matchedImages = duplicateCandidate?.matched_images ?? [];
  const matchedImageUrls = new Set(
    matchedImages.flatMap(image => [image.listing_image_url, image.candidate_image_url]),
  );
  const remainingImages = listings.map(listing => ({
    listing,
    images: imagesForListing(listing).filter(image => !matchedImageUrls.has(image)),
  }));
  const comparisonFields = visibleFields.filter(field => showAllFields || fieldConflict(field) || EDITABLE_FIELDS.includes(field.key));
  const navigationTabs = showAllSections ? tabs : tabs.filter(group => group.key === activeGroup.key || group.fields.some(field =>
    listings.some(listing => listing[field.key] != null && listing[field.key] !== '' && listing[field.key] !== false)
  ) || (group.key === 'gruppen' && listings.some(listing => listing.linked_groups?.length))
    || (group.key === 'bebauungsplaene' && (mergePreview?.buildingplans.length || listings.some(listing => listing.linked_buildingplans?.length))));

  return (
    <main className="mx-auto max-w-screen-2xl p-4 sm:p-6">
      <div className="mb-5 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 ref={headingRef} className="text-xl font-semibold text-gray-900">Inserate zusammenführen</h1>
            <p className="mt-1 text-sm text-gray-600">Duplikatprüfung</p>
          </div>
          <button type="button" onClick={() => navigate(`/inserat/${sourceIds[0]}`)} disabled={mergeMutation.isPending} className="btn btn-outline shrink-0 px-3 py-1.5">
            Abbrechen
          </button>
        </div>
        <div aria-label="Zu vergleichende Inserate" className="grid min-w-0 gap-3 sm:grid-cols-2">
          {listings.map((listing, index) => (
            <div key={listing.id} className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="mb-1 text-xs font-medium text-gray-500">Inserat {index + 1} · #{listing.id}</p>
              <p title={listing.title} className="line-clamp-2 break-words text-sm font-medium leading-5 text-gray-900">
                {listing.title}
              </p>
            </div>
          ))}
        </div>
      </div>

      <nav aria-label="Zusammenführung" className="mb-6 border-b border-gray-200">
        <ol className="grid grid-cols-3 gap-2">
          {STAGES.map((title, index) => (
            <li key={title}>
              <button type="button" onClick={() => goToStage(index)} aria-label={`${index + 1} ${title}`} aria-current={stage === index ? 'step' : undefined}
                disabled={mergeMutation.isPending || (index > 0 && !duplicateConfirmed) || (index === 2 && (missingObjectChoice || pendingSections.length > 0 || !mergePreview || previewLoading || !!previewError))}
                className={`flex min-h-12 w-full items-center gap-3 border-b-2 px-3 py-3 text-left text-sm font-medium disabled:opacity-50 ${stage === index ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600'}`}>
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs">{index + 1}</span><span className="hidden sm:inline">{title}</span>
              </button>
            </li>
          ))}
        </ol>
        <p className="pb-3 text-sm font-medium text-gray-700 sm:hidden">Schritt {stage + 1} von {STAGES.length}: {STAGES[stage]}</p>
      </nav>

      {stage === 0 && <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-semibold">Handelt es sich um dasselbe Angebot?</h2></div>}
      {stage === 0 && <section aria-labelledby="matched-images-heading" className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="mb-3">
          <h2 id="matched-images-heading" className="text-base font-semibold text-blue-950">Ähnliche Bilder</h2>
          <p className="mt-1 text-sm text-blue-900">
            {matchedImages.length > 0
              ? `${matchedImages.length} Bild${matchedImages.length === 1 ? '' : 'er'} wurde${matchedImages.length === 1 ? '' : 'n'} zwischen den Inseraten als ähnlich erkannt.`
              : 'Für dieses Paar sind keine einzelnen Bildtreffer gespeichert.'}
          </p>
        </div>
        {matchedImages.length > 0 && (
          <ul className="grid gap-3 sm:grid-cols-2">
            {matchedImages.map((image, index) => (
              <li key={`${image.listing_image_url}-${image.candidate_image_url}-${index}`} className="rounded-md border border-blue-200 bg-white p-2">
                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                  <figure className="min-w-0">
                    <figcaption className="mb-1 truncate text-xs text-gray-600">{listings[0].title}</figcaption>
                    <img src={image.listing_image_url} alt={`${listings[0].title}: ähnliches Bild`} className="aspect-[4/3] w-full rounded object-cover" />
                  </figure>
                  <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-900">Ähnlich</span>
                  <figure className="min-w-0">
                    <figcaption className="mb-1 truncate text-xs text-gray-600">{listings[1].title}</figcaption>
                    <img src={image.candidate_image_url} alt={`${listings[1].title}: ähnliches Bild`} className="aspect-[4/3] w-full rounded object-cover" />
                  </figure>
                </div>
              </li>
            ))}
          </ul>
        )}
        <details className="mt-4 border-t border-blue-200 pt-3">
          <summary className="cursor-pointer text-sm font-medium text-blue-900">
            Weitere Bilder durchsuchen ({remainingImages.reduce((count, entry) => count + entry.images.length, 0)})
          </summary>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            {remainingImages.map(({ listing, images }) => (
              <section key={listing.id} aria-label={`Weitere Bilder von ${listing.title}`}>
                <h3 className="mb-2 text-sm font-semibold text-gray-800">{listing.title}</h3>
                {images.length > 0 ? (
                  <ul className="grid grid-cols-3 gap-2">
                    {images.map((image, index) => (
                      <li key={`${image}-${index}`}>
                        <img src={image} alt={`Weiteres Bild ${index + 1} von ${listing.title}`} loading="lazy" className="aspect-square w-full rounded object-cover" />
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-sm text-gray-600">Keine weiteren Bilder vorhanden.</p>}
              </section>
            ))}
          </div>
        </details>
      </section>}
      {stage === 0 && <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {listings.map(listing => (
          <section key={listing.id} className="border-b border-gray-200 pb-4">
            <PlatformBadges platforms={listing.platforms} fallback={listing.platform} />
            <h2 className="mt-1 text-base font-semibold text-gray-900">{listing.title}</h2>
            {stage === 0 && <dl className="mt-3 space-y-2 text-sm">
              <div><dt className="text-gray-500">Standort</dt><dd>{listing.linked_object_labels?.adresse_id || [listing.address || listing.scraped_address, listing.zipcode || listing.scraped_zipcode, listing.city || listing.scraped_city].filter(Boolean).join(', ') || 'Nicht bekannt'}</dd></div>
              <div><dt className="text-gray-500">Objekt</dt><dd>{listing.linked_object_labels?.einheit_id || 'Keine Verknüpfung'}</dd></div>
              <div><dt className="text-gray-500">Vermieter</dt><dd>{listing.linked_object_labels?.vermieter_id || listing.host_name || 'Nicht bekannt'}</dd></div>
            </dl>}
            <a href={listing.resolved_url || listing.listing_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm text-blue-700 hover:underline">Angebot öffnen</a>
          </section>
        ))}
      </div>}

      {stage === 1 && <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-y border-gray-200 py-3">
        <p role="status" className={`text-sm font-semibold ${pendingSections.length ? 'text-amber-900' : 'text-green-800'}`}>
          {pendingSections.length ? `${pendingSections.length} ${pendingSections.length === 1 ? 'Bereich braucht' : 'Bereiche brauchen'} eine Entscheidung` : 'Alle Entscheidungen getroffen'}
        </p>
        {pendingSections.length > 0 && !sectionNeedsDecision(activeGroup) && <button type="button" onClick={() => goToStep(tabs.indexOf(pendingSections[0]))} className="btn btn-text hover:underline font-medium text-sm">Offene Entscheidung öffnen</button>}
      </div>}
      {stage === 1 && <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
      <div className="lg:hidden">
        <div className="flex items-center justify-between gap-3"><label htmlFor="merge-section" className="text-sm font-medium text-gray-700">Bereich</label><label className="inline-flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={showAllSections} onChange={event => setShowAllSections(event.target.checked)} />Alle Bereiche</label></div>
        <select id="merge-section" value={activeGroup.key} onChange={event => goToStep(tabs.findIndex(group => group.key === event.target.value))} className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2">
          {navigationTabs.map(group => <option key={group.key} value={group.key}>{group.title}{sectionNeedsDecision(group) ? ' · Offen' : ''}</option>)}
        </select>
      </div>
      <nav aria-label="Bereiche" className="hidden lg:sticky lg:top-0 lg:block">
        <div className="mb-3 flex min-h-9 items-center justify-between gap-2"><h2 className="text-sm font-semibold text-gray-900">Bereiche</h2><label className="inline-flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={showAllSections} onChange={event => setShowAllSections(event.target.checked)} />Alle</label></div>
        <ol className="space-y-1">
          {navigationTabs.map(group => (
            <li key={group.key}>
              <button
                type="button"
                aria-current={activeGroup.key === group.key ? 'page' : undefined}
                onClick={() => goToStep(tabs.indexOf(group))}
                disabled={mergeMutation.isPending}
                className={`flex min-h-12 w-full items-center justify-between gap-2 border-l-2 px-3 py-2 text-left text-sm font-medium disabled:opacity-50 ${
                  activeGroup.key === group.key ? 'border-blue-600 bg-blue-50 text-blue-900' : sectionNeedsDecision(group) ? 'border-transparent text-gray-900 hover:bg-amber-50' : 'border-transparent text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="min-w-0 break-words">{group.title}</span>
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${sectionNeedsDecision(group) ? 'bg-amber-100 text-amber-900' : 'text-gray-500'}`}>{sectionNeedsDecision(group) ? 'Offen' : reviewedSections.includes(group.key) ? 'Geprüft' : 'Übernommen'}</span>
              </button>
            </li>
          ))}
        </ol>
      </nav>

          <section aria-labelledby="merge-step-heading" className="min-w-0">
            <div className="mb-4 flex min-h-9 flex-wrap items-center justify-between gap-3">
              <h2 id="merge-step-heading" className="text-lg font-semibold text-gray-900">{activeGroup.title}</h2>
              <label className="inline-flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={showAllFields} onChange={event => setShowAllFields(event.target.checked)} />Alle Angaben</label>
            </div>
            {objectField && savedObjects.length > 0 && (
              <fieldset className="mb-4 border-y border-gray-200 py-4">
                <legend className="text-sm font-semibold text-gray-900">
                  {OBJECT_QUESTIONS[objectField]}
                </legend>
                <div className="flex flex-wrap gap-x-6 gap-y-3">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                    <input type="radio" name={`${objectField}-mode`} checked={sectionMode === 'link'} onChange={() => setSectionMode('link')} />
                    {activeGroup.title} übernehmen
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                    <input type="radio" name={`${objectField}-mode`} checked={sectionMode === (objectField === 'adresse_id' || objectField === 'vermieter_id' ? 'individual' : 'none')} onChange={() => setSectionMode(objectField === 'adresse_id' || objectField === 'vermieter_id' ? 'individual' : 'none')} />
                    {objectField === 'adresse_id' || objectField === 'vermieter_id' ? 'Angaben einzeln bearbeiten' : 'Ohne Verknüpfung übernehmen'}
                  </label>
                </div>
              </fieldset>
            )}
            {objectField && sectionMode !== 'individual' && sectionMode !== 'none' && savedObjects.length > 0 && (
              <ul className="mb-4 grid gap-3 sm:grid-cols-2">
                {savedObjects.map(listing => (
                  <li key={listing[objectField]} className="min-w-0">
                    <label className={`flex h-full cursor-pointer items-start gap-3 rounded border p-3 ${sectionMode === 'link' && fieldSources[objectField] === listing.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white'}`}>
                      <input
                        type="radio"
                        name={`saved-${objectField}`}
                        checked={sectionMode === 'link' && fieldSources[objectField] === listing.id}
                        disabled={!compatibleObject(listing)}
                        onChange={() => {
                          setSectionMode('link');
                          const chain = objectField === 'einheit_id' ? ['adresse_id', 'haus_id', 'einheit_id'] : objectField === 'haus_id' ? ['adresse_id', 'haus_id'] : [objectField];
                          setFieldSourceOverrides(current => ({ ...current, ...Object.fromEntries(chain.filter(field => listing[field as ObjectField] != null).map(field => [field, listing.id])) }));
                          setObjectModes(current => ({ ...current, ...Object.fromEntries(chain.filter(field => listing[field as ObjectField] != null).map(field => [field, 'link'])) }));
                        }}
                        className="mt-1"
                      />
                      <span className="min-w-0">
                        <span className="block text-xs font-medium text-gray-500">
                          {activeGroup.title} #{listing[objectField]}
                        </span>
                        <span className="block break-words text-sm font-semibold text-gray-900">
                          {listing.linked_object_labels?.[objectField] ?? `#${listing[objectField]}`}
                        </span>
                        {objectField === 'adresse_id' && (
                          <span className="mt-1 block text-sm text-gray-600">
                            {[listing.zipcode, listing.city, listing.linked_object_labels?.haus_id, listing.linked_object_labels?.einheit_id].filter(Boolean).join(' · ')}
                          </span>
                        )}
                        <span className="mt-2 block"><PlatformBadges platforms={listing.platforms} fallback={listing.platform} /></span>
                        {objectField === 'einheit_id' && (
                          <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
                            {activeGroup.fields.filter(field => field.key !== 'size_sqm'
                              && Object.hasOwn(listing.linked_unit_values ?? {}, field.key)).map(field => (
                              <div key={field.key}>
                                <dt className="text-gray-500">{field.label}</dt>
                                <dd className="text-gray-900">{displayValue(listing.linked_unit_values?.[field.key as keyof NonNullable<Listing['linked_unit_values']>])}</dd>
                              </div>
                            ))}
                          </dl>
                        )}
                        {!compatibleObject(listing) && <span className="mt-1 block text-xs text-amber-800">Gehört nicht zur gewählten {objectField === 'haus_id' ? 'Adresse' : 'Hausverknüpfung'}.</span>}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            {objectField && savedObjects.length === 0 && <p className="mb-4 text-sm text-gray-500">Keine gespeicherte Verknüpfung vorhanden.</p>}
            {collectionKey && (
              <div className="border-y border-gray-200 py-3">
                {collectionKey === 'linked_buildingplans' && <p className="mb-2 text-xs text-gray-500">{mergePreview ? 'Standortabgleich abgeschlossen' : 'Standortabgleich ausstehend'}</p>}
                {collectionObjects.length === 0 && <p className="text-sm text-gray-500">Keine verknüpften {activeGroup.title} vorhanden.</p>}
                {collectionObjects.map(item => (
                  <label key={item.id} className="flex items-center gap-3 py-3 text-sm text-gray-900">
                    {collectionKey !== 'linked_buildingplans' && <input type="checkbox" checked={selectedCollectionIds.includes(item.id)} onChange={event => setCollectionSelections(current => ({
                      ...current, [collectionKey]: event.target.checked ? [...selectedCollectionIds, item.id] : selectedCollectionIds.filter(id => id !== item.id),
                    }))} />}
                    {item.label} (#{item.id})
                  </label>
                ))}
              </div>
            )}
            {comparisonFields.length === 0 && !objectField && !collectionKey && <p className="py-4 text-sm text-gray-600">Keine widersprüchlichen Angaben.</p>}
            {comparisonFields.length > 0 && (
            <div className="divide-y divide-gray-200 border-y border-gray-200">
                  {comparisonFields.map(field => {
                    const linkedAddressField = addressMode === 'link' && addressSource?.adresse_id != null
                      && field.key !== 'adresse_id' && LINKED_ADDRESS_FIELDS.includes(field.key);
                    return (
                    <fieldset key={field.key} className="min-w-0 py-5">
                      <legend className="flex flex-wrap items-center gap-2 pt-4 text-sm font-semibold text-gray-900">{field.label}
                        {fieldConflict(field) && sectionNeedsDecision(activeGroup) && <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">Auswahl prüfen</span>}
                      </legend>
                      <div className="grid items-stretch gap-3 sm:grid-cols-2">
                      {listings.map(listing => {
                        const selected = fieldSources[field.key] === listing.id;
                        return (
                            <label
                              key={listing.id}
                              className={`flex min-w-0 items-start gap-3 rounded border p-3 ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white hover:border-gray-400'} ${linkedAddressField ? 'cursor-default' : 'cursor-pointer'}`}
                              title={linkedAddressField ? 'Wird mit dem gewählten Standort übernommen' : undefined}
                            >
                              <input
                                type="radio"
                                name={field.key}
                                aria-label={`${field.label} aus Inserat #${listing.id}`}
                                checked={selected}
                                disabled={linkedAddressField}
                                onChange={() => {
                                  setFieldSourceOverrides(current => ({ ...current, [field.key]: listing.id }));
                                  setFieldValueOverrides(current => {
                                    const next = { ...current };
                                    delete next[field.key as keyof typeof next];
                                    return next;
                                  });
                                }}
                                className="mt-1"
                              />
                              <span className="min-w-0 flex-1">
                                <span className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500"><span className="inline-flex items-center gap-2"><PlatformBadges platforms={listing.platforms} fallback={listing.platform} />#{listing.id}</span>{selected && <span className="font-medium text-blue-800">{Object.hasOwn(fieldSourceOverrides, field.key) || reviewedSections.includes(activeGroup.key) ? 'Gewählt' : 'Vorgeschlagen'}</span>}</span>
                                <span className={`block break-words whitespace-pre-wrap text-sm ${listing[field.key] == null ? 'text-gray-400' : 'text-gray-900'}`}>
                                {listing.linked_object_labels?.[field.key]
                                  ? `${listing.linked_object_labels[field.key]} (#${listing[field.key]})`
                                  : displayValue(field.key === 'size' ? listing.size || (listing.size_sqm != null ? `${listing.size_sqm} m²` : null) : listing[field.key])}
                                </span>
                              </span>
                            </label>
                        );
                      })}
                      </div>
                      <div className="mt-3 border-l-2 border-blue-300 pl-3">
                        <p className="mb-1 text-xs font-medium text-gray-500">Wird übernommen</p>
                        {EDITABLE_FIELDS.includes(field.key) && sectionMode === 'individual' ? (
                          <input
                            aria-label={`${field.label}: Ergebnis`}
                            type="text"
                            value={resultValue(field) == null ? '' : String(resultValue(field))}
                            onChange={event => setFieldValueOverrides(current => ({ ...current,
                              [field.key]: event.target.value === '' ? null : event.target.value,
                            }))}
                            className="w-full rounded border border-gray-300 bg-white px-2 py-1.5"
                          />
                        ) : <span className="block break-words whitespace-pre-wrap text-sm text-gray-900">{displayValue(resultValue(field))}</span>}
                      </div>
                    </fieldset>
                    );
                  })}
            </div>
            )}
          </section>
          <details aria-label="Vorgeschlagenes Ergebnis" className="border-y border-gray-200 py-3 lg:col-start-2">
            <summary className="cursor-pointer text-sm font-semibold text-gray-700">Verknüpfungen im Ergebnis</summary>
            <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">{tabs.flatMap(group => group.objectField ? [group] : []).map(group => (
              <div key={group.key}><dt className="text-gray-500">{group.title}</dt><dd className="break-words">{getObjectMode(group.objectField!) === null ? 'Auswahl offen' : getObjectMode(group.objectField!) !== 'link' ? 'Ohne Verknüpfung' : displayValue(resultValue(group.fields[0]))}</dd></div>
            ))}</dl>
          </details>
      </div>}

      {stage === 2 && <section aria-label="Ergebnis prüfen" className="space-y-5">
        <h2 className="text-lg font-semibold">Zusammenführung prüfen und bestätigen</h2>
        {tabs.map(group => {
          const mode = group.objectField ? getObjectMode(group.objectField) : null;
          const key = group.key === 'gruppen' ? 'linked_groups' : group.key === 'bebauungsplaene' ? 'linked_buildingplans' : null;
          const objects = key ? [...new Map([
            ...listings.flatMap(listing => listing[key] ?? []),
            ...(key === 'linked_buildingplans' ? mergePreview?.buildingplans ?? [] : []),
          ].map(item => [item.id, item])).values()] : [];
          const retained = key === 'linked_buildingplans' ? mergePreview?.buildingplans.map(item => item.id) ?? [] : key ? collectionSelections[key] ?? objects.map(item => item.id) : [];
          const reviewFields = group.fields.filter(field => field.key !== 'size_sqm' && (
            field.key === group.objectField ? listings.some(listing => listing[field.key] != null) : resultValue(field) != null && resultValue(field) !== ''
          ));
          if (key ? objects.length === 0 : reviewFields.length === 0) return null;
          return <section key={group.key} className="border-b border-gray-200 pb-4">
            <div className="mb-2 flex items-center justify-between gap-3"><h3 className="text-sm font-semibold">{group.title}</h3><button type="button" onClick={() => {goToStep(tabs.indexOf(group)); goToStage(1);}} aria-label={`${group.title} ändern`} className="btn btn-text hover:underline text-sm">Ändern</button></div>
            {key ? <ul className="text-sm">{objects.length === 0 && <li>Keine Verknüpfungen</li>}{objects.map(item => <li key={item.id} className={retained.includes(item.id) ? 'text-gray-900' : 'text-gray-500'}>{item.label}: {retained.includes(item.id) ? 'Wird übernommen' : 'Wird nicht übernommen'}</li>)}</ul> : <dl className="grid gap-3 text-sm sm:grid-cols-2">
              {reviewFields.map(field => <div key={field.key}><dt className="text-gray-500">{field.label}</dt><dd className="break-words whitespace-pre-wrap">{field.key === group.objectField && mode !== 'link' ? 'Ohne Verknüpfung' : displayValue(resultValue(field))}</dd></div>)}
            </dl>}
          </section>;
        })}
        <p className="text-sm text-gray-700">Ein neues Inserat wird angelegt. Die Quellinserate bleiben erhalten und werden ausgeblendet. Gespeicherte Objekte bleiben unverändert. Die Duplikatprüfung wird abgeschlossen.</p>
      </section>}

      {mergeMutation.error && <div className="mt-4 text-sm text-red-700">{mergeMutation.error.message}</div>}
      {rejectMutation.error && <div role="alert" className="mt-4 text-sm text-red-700">{rejectMutation.error.message}</div>}
      {previewError && <p role="alert" className="mt-4 text-sm text-red-700">{previewError.message}</p>}
      {(previewError || mergeMutation.error) && <button type="button" onClick={() => refreshPreview()} className="btn btn-text mt-2 hover:underline text-sm">Ergebnis aktualisieren</button>}
      {stage === 1 && (missingObjectChoice || pendingSections.length > 0) && (
        <p role="status" className="mt-4 text-sm text-amber-800">
          {invalidHierarchy ? 'Adresse, Haus und Objekt passen nicht zusammen.' : `Auswahl offen: ${[...new Set([...unresolvedTabs, ...pendingSections].map(group => group.title))].join(', ')}`}
        </p>
      )}
      <div className="sticky bottom-0 mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50/95 py-4">
        {stage > 0 && <button
          type="button"
          onClick={() => goToStage(stage - 1)}
          disabled={mergeMutation.isPending}
          className="btn btn-outline bg-white"
        >
          Zurück
        </button>}
        {stage === 0 && <div className="ml-auto flex flex-wrap gap-3">
          <button type="button" onClick={() => rejectMutation.mutate()} disabled={rejectMutation.isPending} className="btn btn-outline bg-white">{rejectMutation.isPending ? 'Wird abgeschlossen…' : 'Kein Duplikat'}</button>
          <button type="button" onClick={() => {setDuplicateConfirmed(true); if (pendingSections[0]) goToStep(tabs.indexOf(pendingSections[0])); goToStage(1);}} disabled={rejectMutation.isPending} className="btn btn-primary">Gleiches Angebot</button>
        </div>}
        {stage === 1 && <div className="flex flex-wrap gap-3">
          {sectionNeedsDecision(activeGroup) && <button type="button" onClick={() => {
            setReviewedSections(current => [...new Set([...current, activeGroup.key])]);
            const next = pendingSections.find(group => group.key !== activeGroup.key);
            if (next) goToStep(tabs.indexOf(next));
          }} disabled={sectionMode === null && !!objectField || invalidHierarchy} className="btn btn-outline border-primary-200 text-primary-700 enabled:hover:bg-primary-50 focus-visible:ring-primary-500 bg-white px-3">Auswahl übernehmen</button>}
          {!missingObjectChoice && pendingSections.length === 0 && <button type="button" onClick={() => goToStage(2)} disabled={!mergePreview || previewLoading || !!previewError} className="btn btn-primary">
            {previewLoading ? 'Ergebnis wird geprüft…' : 'Ergebnis prüfen'}
          </button>}
        </div>}
        {stage === 2 && (
          <button
            type="button"
            onClick={() => mergeMutation.mutate()}
            disabled={mergeMutation.isPending || missingObjectChoice || !mergePreview || previewLoading || !!previewError || Object.keys(fieldSources).length === 0}
            className="btn btn-primary"
          >
            {mergeMutation.isPending ? 'Wird zusammengeführt…' : 'Zusammenführung bestätigen'}
          </button>
        )}
      </div>
    </main>
  );
}